import { css, html, LitElement, nothing } from "lit";
import { type PixelIconName, renderPixelIcon } from "../shared/pixel-icons";
import { windowsMePrimitives } from "../shared/windows-me-styles";

type DragPhase = "start" | "move" | "end";

export class WindowFrame extends LitElement {
	static properties = {
		active: { type: Boolean, reflect: true },
		closable: { type: Boolean },
		draggable: { type: Boolean },
		icon: { type: String },
		maximizable: { type: Boolean },
		maximized: { type: Boolean, reflect: true },
		minimizable: { type: Boolean },
		minimized: { type: Boolean, reflect: true },
		status: { type: String },
		title: { type: String },
		windowId: { type: String, attribute: "window-id" },
	};

	static styles = [
		windowsMePrimitives,
		css`
			:host {
				display: block;
				inline-size: min(100%, var(--window-frame-width, 320px));
				min-inline-size: min(280px, 100%);
				max-inline-size: calc(100vw - 12px);
				color: var(--shell-text);
			}

			:host([minimized]) {
				display: none;
			}

			.frame {
				display: grid;
				grid-template-rows: auto auto minmax(0, 1fr) auto;
				inline-size: 100%;
				block-size: var(--window-frame-height, auto);
				min-block-size: var(--window-frame-min-height, 180px);
				box-sizing: border-box;
				background: var(--shell-surface);
				border-block-start: 2px solid var(--shell-surface-light);
				border-inline-start: 2px solid var(--shell-surface-light);
				border-inline-end: 2px solid var(--shell-surface-dark);
				border-block-end: 2px solid var(--shell-surface-dark);
				box-shadow:
					inset 1px 1px 0 var(--shell-surface),
					inset -1px -1px 0 var(--shell-surface-mid),
					2px 2px 0 rgb(0 0 0 / 20%);
				overflow: hidden;
			}

			.titlebar {
				grid-row: 1;
				display: grid;
				grid-template-columns: auto minmax(0, 1fr) auto;
				align-items: center;
				gap: 2px;
				block-size: 20px;
				margin: 2px 2px 0;
				padding: 1px 2px;
				box-sizing: border-box;
				color: #d4d0c8;
				background: linear-gradient(90deg, #808080 0%, #b5b5b5 100%);
				touch-action: none;
				user-select: none;
			}

			:host([active]) .titlebar {
				color: #ffffff;
				background: linear-gradient(90deg, #0a246a 0%, #a6caf0 100%);
			}

			.titlebar:focus-visible {
				outline: 1px dotted currentColor;
				outline-offset: -3px;
			}

			.app-mark {
				display: grid;
				place-items: center;
				inline-size: 16px;
				block-size: 16px;
			}

			.app-mark .pixel-icon {
				inline-size: 16px;
				block-size: 16px;
			}

			.title {
				min-inline-size: 0;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
				font: 700 11px/14px "MS Sans Serif", Tahoma, var(--shell-font);
				letter-spacing: 0;
				text-align: start;
			}

			.caption-buttons {
				display: inline-flex;
				align-items: center;
				gap: 1px;
			}

			.caption-button {
				position: relative;
				display: inline-grid;
				place-items: center;
				flex: 0 0 auto;
				inline-size: 16px;
				block-size: 14px;
				padding: 0;
				color: var(--shell-text);
				background: var(--shell-surface);
				border-block-start: 1px solid var(--shell-surface-light);
				border-inline-start: 1px solid var(--shell-surface-light);
				border-inline-end: 1px solid #000000;
				border-block-end: 1px solid #000000;
				box-shadow:
					inset -1px -1px 0 var(--shell-surface-mid),
					inset 1px 1px 0 var(--shell-surface-light);
				cursor: pointer;
			}

			.caption-button[data-kind="close"] {
				margin-inline-start: 1px;
			}

			.caption-button:hover {
				background: #e2ded6;
			}

			.caption-button:active {
				border-block-start-color: var(--shell-surface-dark);
				border-inline-start-color: var(--shell-surface-dark);
				border-inline-end-color: var(--shell-surface-light);
				border-block-end-color: var(--shell-surface-light);
				box-shadow: inset 1px 1px 0 var(--shell-surface-mid);
			}

			.caption-button:active::before,
			.caption-button:active::after {
				translate: 1px 1px;
			}

			.caption-button:focus-visible {
				outline: 1px dotted currentColor;
				outline-offset: -3px;
			}

			.caption-button[data-kind="minimize"]::before {
				content: "";
				inline-size: 7px;
				block-size: 6px;
				border-block-end: 2px solid currentColor;
			}

			.caption-button[data-kind="maximize"]::before {
				content: "";
				inline-size: 8px;
				block-size: 7px;
				border: 1px solid currentColor;
				border-block-start-width: 2px;
				box-sizing: border-box;
			}

			.caption-button[data-kind="restore"]::before {
				content: "";
				inline-size: 7px;
				block-size: 6px;
				border: 1px solid currentColor;
				box-sizing: border-box;
				box-shadow: 2px -2px 0 -1px var(--shell-surface), 2px -2px 0 0 currentColor;
			}

			.caption-button[data-kind="close"]::before,
			.caption-button[data-kind="close"]::after {
				content: "";
				position: absolute;
				inset-inline-start: 4px;
				inset-block-start: 6px;
				inline-size: 8px;
				block-size: 2px;
				background: currentColor;
				transform: rotate(45deg);
				transform-origin: center;
			}

			.caption-button[data-kind="close"]::after {
				transform: rotate(-45deg);
			}

			.menu {
				grid-row: 2;
				min-block-size: 20px;
				padding: 0 6px;
				font: 400 11px/20px var(--shell-font);
				color: var(--shell-text);
			}

			.content {
				grid-row: 3;
				min-block-size: 0;
				padding: 8px;
				background: var(--shell-surface);
				overflow: var(--window-frame-content-overflow, visible);
			}

			.content > slot[name="app"]::slotted(*) {
				display: block;
			}

			.statusbar {
				grid-row: 4;
				display: flex;
				align-items: center;
				min-block-size: 22px;
				padding: 0 4px;
				font: 400 11px/1 var(--shell-font);
				border-block-start: 1px solid var(--shell-surface-mid);
			}

			.status-text {
				padding: 2px 4px;
			}

		`,
	];

	active = true;

	closable = true;

	draggable = true;

	icon: PixelIconName = "window";

	maximizable = true;

	maximized = false;

	minimizable = true;

	minimized = false;

	status = "";

	title = "";

	windowId = "";

	#dragPointerId: number | null = null;

	#dragStartX = 0;

	#dragStartY = 0;

	#lastClientX = 0;

	#lastClientY = 0;

	render() {
		const title = this.title || "Window";
		const maximizeKind = this.maximized ? "restore" : "maximize";

		return html`
			<section
				class="frame"
				role="group"
				aria-label=${title}
				@pointerdown=${this.#handleFramePointerDown}
			>
				<div
					class="titlebar"
					part="titlebar"
					role="toolbar"
					tabindex="0"
					aria-label=${`${title} window title bar`}
					@dblclick=${this.#handleTitlebarDoubleClick}
					@keydown=${this.#handleTitlebarKeyDown}
					@pointerdown=${this.#handleTitlebarPointerDown}
					@pointermove=${this.#handleTitlebarPointerMove}
					@pointerup=${this.#handleTitlebarPointerUp}
					@pointercancel=${this.#handleTitlebarPointerUp}
				>
					<span class="app-mark" aria-hidden="true">
						${renderPixelIcon(this.icon, "")}
					</span>
					<span class="title">${title}</span>
					<span class="caption-buttons">
						${
							this.minimizable
								? html`
									<button
										class="caption-button"
										data-kind="minimize"
										type="button"
										aria-label=${`Minimize ${title}`}
										title=${`Minimize ${title}`}
										@click=${this.#handleMinimizeClick}
									></button>
								`
								: nothing
						}
						${
							this.maximizable
								? html`
									<button
										class="caption-button"
										data-kind=${maximizeKind}
										type="button"
										aria-label=${this.maximized ? `Restore ${title}` : `Maximize ${title}`}
										title=${this.maximized ? `Restore ${title}` : `Maximize ${title}`}
										@click=${this.#handleMaximizeClick}
									></button>
								`
								: nothing
						}
						${
							this.closable
								? html`
									<button
										class="caption-button"
										data-kind="close"
										type="button"
										aria-label=${`Close ${title}`}
										title=${`Close ${title}`}
										@click=${this.#handleCloseClick}
									></button>
								`
								: nothing
						}
					</span>
				</div>
				${
					this.#hasSlot("menu")
						? html`
							<nav class="menu" aria-label=${`${title} menu`}>
								<slot name="menu"></slot>
							</nav>
						`
						: nothing
				}
				<div class="content">
					<slot name="app"></slot>
					<slot></slot>
				</div>
				${
					this.status || this.#hasSlot("status")
						? html`
							<footer class="statusbar" aria-live="polite">
								<slot name="status">
									<span class="sunken-panel status-text">${this.status}</span>
								</slot>
							</footer>
						`
						: nothing
				}
			</section>
		`;
	}

	#handleFramePointerDown(event: PointerEvent) {
		if (this.active) return;
		const startedOnTitlebar = event
			.composedPath()
			.some(
				(target) =>
					target instanceof HTMLElement &&
					target.classList.contains("titlebar"),
			);
		if (startedOnTitlebar) return;
		window.setTimeout(() => this.#emit("window-focus-request"), 0);
	}

	#handleTitlebarDoubleClick(event: MouseEvent) {
		if (!this.maximizable) return;
		if (event.target instanceof Element && event.target.closest("button"))
			return;
		event.preventDefault();
		this.#emit("window-toggle-maximize", { maximized: !this.maximized });
	}

	#handleTitlebarKeyDown(event: KeyboardEvent) {
		if (event.target instanceof Element && event.target.closest("button"))
			return;
		if ((event.key === "Enter" || event.key === " ") && this.maximizable) {
			event.preventDefault();
			this.#emit("window-toggle-maximize", { maximized: !this.maximized });
		}
		if (event.key === "Escape" && this.#dragPointerId != null) {
			this.#finishDrag("end");
		}
	}

	#handleTitlebarPointerDown(event: PointerEvent) {
		if (!this.draggable || event.button !== 0) return;
		if (event.target instanceof Element && event.target.closest("button"))
			return;
		this.#dragPointerId = event.pointerId;
		this.#dragStartX = event.clientX;
		this.#dragStartY = event.clientY;
		this.#lastClientX = event.clientX;
		this.#lastClientY = event.clientY;
		this.#emitDrag("start", event);
		if (!this.active) this.#emit("window-focus-request");
		const titlebar = event.currentTarget;
		if (titlebar instanceof HTMLElement)
			titlebar.setPointerCapture(event.pointerId);
	}

	#handleTitlebarPointerMove(event: PointerEvent) {
		if (this.#dragPointerId !== event.pointerId) return;
		this.#lastClientX = event.clientX;
		this.#lastClientY = event.clientY;
		this.#emitDrag("move", event);
	}

	#handleTitlebarPointerUp(event: PointerEvent) {
		if (this.#dragPointerId !== event.pointerId) return;
		this.#lastClientX = event.clientX;
		this.#lastClientY = event.clientY;
		this.#finishDrag("end", event);
	}

	#handleMinimizeClick(event: Event) {
		event.stopPropagation();
		this.#emit("window-toggle-minimize", { minimized: !this.minimized });
	}

	#handleMaximizeClick(event: Event) {
		event.stopPropagation();
		this.#emit("window-toggle-maximize", { maximized: !this.maximized });
	}

	#handleCloseClick(event: Event) {
		event.stopPropagation();
		this.#emit("window-close-request");
	}

	#finishDrag(phase: DragPhase, event?: PointerEvent) {
		if (event) this.#emitDrag(phase, event);
		this.#dragPointerId = null;
	}

	#emit(name: string, detail: Record<string, unknown> = {}) {
		this.dispatchEvent(
			new CustomEvent(name, {
				bubbles: true,
				composed: true,
				detail: {
					title: this.title,
					windowId: this.windowId,
					...detail,
				},
			}),
		);
	}

	#emitDrag(phase: DragPhase, event: PointerEvent) {
		this.dispatchEvent(
			new CustomEvent(`window-drag-${phase}`, {
				bubbles: true,
				composed: true,
				detail: {
					clientX: event.clientX,
					clientY: event.clientY,
					deltaX: event.clientX - this.#dragStartX,
					deltaY: event.clientY - this.#dragStartY,
					lastClientX: this.#lastClientX,
					lastClientY: this.#lastClientY,
					pointerId: event.pointerId,
					title: this.title,
					windowId: this.windowId,
				},
			}),
		);
	}

	#hasSlot(name: string) {
		return this.querySelector(`[slot="${name}"]`) !== null;
	}
}

if (!customElements.get("window-frame")) {
	customElements.define("window-frame", WindowFrame);
}
